import type { FieldDefinition, FieldOption, FormConfig } from '../shared/types';
import { resolveDateBound } from '../shared/types';
import { usPhoneDigits, wantsUsPhone } from './phone';

/**
 * Resolve the effective config for a submission. Layers, in order:
 *  1. base partner config
 *  2. the selected FLOW block (simpleConfig / complexConfig) when the partner
 *     offers a per-order flow selector (e.g. vCom: Drop Ship Only vs Full Impl.)
 *  3. the order-type override (hardwareOnly / standardOrder|standard)
 */
export function resolveConfig(formConfig: FormConfig, orderType: string, flow?: string): FormConfig {
  let base: FormConfig = { ...formConfig };
  if (formConfig.showFlowSelector && flow) {
    const block = flow === 'complex' ? formConfig.complexConfig : formConfig.simpleConfig;
    base = { ...base, ...(block || {}) };
  }
  let override: FormConfig = {};
  if (orderType === 'hardwareOnly') override = base.hardwareOnly || {};
  else if (orderType === 'standard') override = base.standardOrder || base.standard || {};
  return { ...base, ...override };
}

export function flowOf(formConfig: FormConfig): 'simple' | 'complex' {
  return formConfig.orderFlowType === 'complex' ? 'complex' : 'simple';
}

/** Options for a partner's per-order flow selector. */
export function flowSelectorOptions(formConfig: FormConfig): { value: string; text: string }[] {
  const opts = formConfig.flowOptions;
  if (Array.isArray(opts) && opts.length) {
    return opts.map((o: any) => (typeof o === 'string' ? { value: o, text: o } : { value: o.value, text: o.text ?? o.value }));
  }
  return [{ value: 'simple', text: 'Drop Ship Only' }, { value: 'complex', text: 'Full Implementation' }];
}

export function defaultFlow(formConfig: FormConfig): string {
  if (formConfig.defaultFlow) return String(formConfig.defaultFlow);
  return flowSelectorOptions(formConfig)[0]?.value || 'simple';
}

export function fieldVisible(field: FieldDefinition, cfg: FormConfig): boolean {
  const m = field.ui_meta || {};
  // An explicitly-set show flag always wins (e.g. showFileAttachments overriding a default).
  if (m.showFlag && cfg[m.showFlag] !== undefined) return !!cfg[m.showFlag];
  if (m.hideWith && cfg[m.hideWith]) return false;
  if (m.alwaysShow || m.defaultShow) return true;
  if (m.showFlag) return !!cfg[m.showFlag];
  return true;
}

/**
 * What a rule has to say about one field's requiredness in the context being
 * rendered. Supplied by evaluate.ts, which has already expanded container
 * targets ('sec:' / 'card:' / 'wnode:') into the fields inside them.
 */
export interface RequirednessCtx {
  /** field keys hidden right now — nothing hidden is ever required */
  hidden?: Set<string>;
  /** keys a firing `require` action names */
  required?: Set<string>;
  /** keys a firing `optional` action names */
  optional?: Set<string>;
  /** keys a firing `lock` action names (NOT ctx.locked, which is the prefill mirror map) */
  lockedByRule?: Set<string>;
  /** keys a firing `unlock` action names */
  unlockedByRule?: Set<string>;
}

/**
 * Is this field required, here, now?
 *
 * The order matters and is not negotiable:
 *  1. HIDDEN wins over everything. A respondent must never be blocked by a
 *     question they cannot see, whatever any rule says.
 *  2. `optional` beats `require`, for exactly that reason one step down: when
 *     two rules disagree, the one that lets the person through wins.
 *  3. `require` then bends what Build says, but only while its rule holds.
 *  4. Otherwise the field's own Build setting, as it always was.
 */
export function fieldRequired(field: FieldDefinition, cfg: FormConfig, rules?: RequirednessCtx): boolean {
  const m = field.ui_meta || {};
  if (rules) {
    if (rules.hidden?.has(field.key)) return false;
    if (rules.optional?.has(field.key)) return false;
    if (rules.required?.has(field.key)) return true;
  }
  if (m.alwaysRequired) return true;
  if (m.requireFlag) {
    // Legacy semantic `showX && requireX !== false`: when the field is shown it is
    // required BY DEFAULT and only optional if the require flag is explicitly false.
    if (m.requiredWhenShown) return fieldVisible(field, cfg) && cfg[m.requireFlag] !== false;
    return !!cfg[m.requireFlag];
  }
  return !!field.required_default;
}

/**
 * Is this field read-only, here, now?
 *
 * Build answers it, a rule can overrule the answer, and `unlock` wins when two
 * rules disagree — the same shape requiredness has, and the same reason: when
 * the rules contradict each other, prefer the reading that lets the respondent
 * act. A locked field is never demanded (see fieldRequired's caller), because
 * locked-and-empty-and-required is a box nobody can get out of.
 */
export function fieldLocked(field: FieldDefinition, rules?: RequirednessCtx): boolean {
  if (rules) {
    if (rules.unlockedByRule?.has(field.key)) return false;
    if (rules.lockedByRule?.has(field.key)) return true;
  }
  return !!field.ui_meta?.locked;
}

/**
 * Conditional visibility on another field's current value (ui_meta.showWhen).
 * `orFlag` is a config flag that also reveals the field (e.g. a partner that
 * always collects a hardware-shipping address). No rule ⇒ always passes.
 */
export function showWhenPasses(field: FieldDefinition, values: Record<string, unknown>, cfg: FormConfig): boolean {
  const sw = field.ui_meta?.showWhen;
  if (!sw) return true;
  if (sw.orFlag && cfg[sw.orFlag]) return true;
  return values[sw.field] === sw.equals;
}

function normalizeOptions(raw: unknown): FieldOption[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((o: any) => (typeof o === 'string' ? { value: o, text: o } : { value: o.value, text: o.text ?? o.value }));
}

export function fieldOptions(field: FieldDefinition, cfg: FormConfig): FieldOption[] {
  const m = field.ui_meta || {};
  if (m.optionsKey && cfg[m.optionsKey]) return normalizeOptions(cfg[m.optionsKey]);
  return field.options || [];
}

/**
 * The answer a field starts from, or undefined for a blank one.
 *
 * A config-driven default (legacy ui_meta.defaultKey) still wins, then the
 * default chosen in the builder. A 'date' default of 'today' resolves on the
 * day the form is opened, which is the only thing "today" can honestly mean.
 * A US phone default is read the way its control stores an answer: digits.
 */
export function fieldDefault(field: FieldDefinition, cfg: FormConfig): unknown {
  const m = field.ui_meta || {};
  if (m.defaultKey && cfg[m.defaultKey] != null) return String(cfg[m.defaultKey]);
  const d = m.defaultValue;
  if (d === undefined || d === null || d === '') return undefined;
  if (Array.isArray(d)) return d.length ? [...d] : undefined;
  if (field.input_type === 'date' && typeof d === 'string') return resolveDateBound(d) ?? d;
  // A US phone holds digits, and its control only ever draws digits — so a
  // default with none (a prompt typed where a placeholder was meant) would be
  // an answer nobody can see, refused on submit from an empty-looking box.
  if (wantsUsPhone(field)) return usPhoneDigits(d) || undefined;
  return d;
}

/** Parse a free-text email list (comma/semicolon/space/slash separated) into a deduped array. */
export function parseEmailList(text: string): string[] {
  if (!text) return [];
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const out: string[] = [];
  for (const token of text.split(/[\s,;/]+/)) {
    const t = token.trim();
    if (!t) continue;
    const low = t.toLowerCase();
    if (['no', 'none', 'noothers', 'no others', 'n/a', 'na'].includes(low)) continue;
    if (re.test(t) && !out.includes(t)) out.push(t);
  }
  return out;
}
